import { GameModel } from "@nolimitcity/slot-game/bin/model/GameModel";
import { APIFeatureBetEventData } from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import { APIBetType } from "@nolimitcity/slot-launcher/bin/interfaces/APIBetType";
import { APIEvent } from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import { ApiPlugin } from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";

import { EventsManager, GameEvents } from "../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../abstracts/flow/AbstractGameMode";
import { ColorSet, log } from "../../abstracts/utils/AbstractConsole";
import { DDD } from "../DDD";
import { reelsConfig, xbet2Name, xbetName } from "../config/DDDConfig";
import { DDDSpecialSymbolNames, DDDSymbolNames } from "../types/DDDEnums";
import { DDDGameData, DDDInitData } from "../types/DDDInterfaces";

export const falseArray: boolean[][] = new Array(reelsConfig.reelsNumber).fill(
    new Array(reelsConfig.rowsNumber).fill(false)
);

export class DDDModel extends GameModel<DDDInitData, DDDGameData> {
    public isRestore: boolean = false;
    public buyFeatureName: string = ``;
    public isLuckyDraw: boolean = false;
    public isXbet: boolean = false;
    public winningPos: boolean[][];

    public xNudgeMap: boolean[] = [];
    public hasXGod: boolean = false;
    public fullXGodReelIndex: number = -1;
    public xGodMap: boolean[] = [];
    public wildMap: boolean[] = [];
    public wildMultipliers: number[] = [];

    constructor(api: ApiPlugin, triggerEvents: boolean = true) {
        super(api, triggerEvents);

        api.events.on(`featureName`, (data: string) => {
            this.buyFeatureName = data;
        });

        const handleBet = (data: APIFeatureBetEventData) => {
            this.isLuckyDraw = data.playerInteraction?.featureName === `LUCKY_DRAW`;

            if (
                data.type == APIBetType.FEATURE_BET &&
                data.playerInteraction.featureName !== xbetName
            ) {
                EventsManager.trigger(GameEvents.ON_FREE_BETS_START);
            }

            if (data.featureName) {
                this.isXbet = data.featureName === xbetName || data.featureName === xbet2Name;

                this.buyFeatureName = data.featureName;
            } else {
                this.isXbet = false;
            }
        };

        DDD.apiPlugin.events.on(APIEvent.BET, (data: APIFeatureBetEventData) => handleBet(data));

        DDD.apiPlugin.events.on(APIEvent.ACTION_SPINS_BET, (data: APIFeatureBetEventData) =>
            handleBet(data)
        );
    }

    protected onInit(data: DDDInitData): void {
        log(`init data`, ColorSet.DARK_RED, data);

        super.onInit(data);

        this.winningPos = [];

        this.isRestore = data.isRestoreState;
    }

    protected onGame(data: DDDGameData): void {
        // const origReels = JSON.parse(JSON.stringify(data.reels));
        // const origEvArea = JSON.parse(JSON.stringify(data.evaluatedArea));
        // console.log(`new data origReels`, origReels);
        // console.log(`new data origEvArea`, origEvArea);

        log(`new data`, ColorSet.RED, data);

        if (!data.boostedBet && !data.wasFeatureBuy) {
            this.buyFeatureName = ``;
        }

        this.isRestore = data.isRestoreState !== undefined ? data.isRestoreState : false;

        this.xNudgeMap = [];
        this.xGodMap = [];
        this.wildMap = [false, false, false, false, false];
        this.wildMultipliers = [];
        this.fullXGodReelIndex = -1;

        const xNudgeWinningPos = JSON.parse(JSON.stringify(this.winningPos));

        this.winningPos = JSON.parse(JSON.stringify(falseArray));
        this.hasXGod = false;

        const clonedReels = JSON.parse(JSON.stringify(data.reels));

        let shouldSkip: boolean = false;
        let multiplierCounter: number = 0;

        clonedReels.forEach((reel: string[], reelIndex: number) => {
            if (
                JSON.stringify(reel) ===
                JSON.stringify([
                    DDDSpecialSymbolNames.XGOD_1,
                    DDDSpecialSymbolNames.XGOD_2,
                    DDDSpecialSymbolNames.XGOD_3,
                ])
            ) {
                this.hasXGod = true;
            }

            const xNudgeCondition: boolean =
                reel.includes(DDDSpecialSymbolNames.XN_1) ||
                reel.includes(DDDSpecialSymbolNames.XN_2) ||
                reel.includes(DDDSpecialSymbolNames.XN_3);

            const xGodCondition: boolean =
                reel.includes(DDDSpecialSymbolNames.XGOD_1) ||
                reel.includes(DDDSpecialSymbolNames.XGOD_2) ||
                reel.includes(DDDSpecialSymbolNames.XGOD_3);

            this.xNudgeMap[reelIndex] = xNudgeCondition;
            this.xGodMap[reelIndex] = xGodCondition;

            const wildCondition: boolean =
                data.evaluatedArea[reelIndex].includes(DDDSpecialSymbolNames.WILD) &&
                !this.xNudgeMap[reelIndex];

            if (wildCondition) {
                multiplierCounter++;
                this.wildMap[reelIndex] = true;
            }

            this.wildMultipliers[reelIndex] = multiplierCounter;

            const stringReel: string = JSON.stringify(reel);
            const stringXGodReel: string = JSON.stringify([
                DDDSpecialSymbolNames.XGOD_1,
                DDDSpecialSymbolNames.XGOD_2,
                DDDSpecialSymbolNames.XGOD_3,
            ]);

            if (stringReel === stringXGodReel && data.brokeTheBank && !shouldSkip) {
                this.winningPos = JSON.parse(JSON.stringify(falseArray));
                this.winningPos[reelIndex][0] = true;
                shouldSkip = true;
                this.fullXGodReelIndex = reelIndex;
            }

            if (!shouldSkip) {
                data.betWayWins.forEach((betWinLine) => {
                    if (reelIndex < betWinLine.length) {
                        reel.forEach((symbol, symbolIndex) => {
                            if (
                                symbol === betWinLine.symbol ||
                                ((symbol === DDDSpecialSymbolNames.WILD ||
                                    symbol === DDDSpecialSymbolNames.SCATTER) &&
                                    data.evaluatedArea[reelIndex][symbolIndex] ===
                                        DDDSpecialSymbolNames.WILD)
                            ) {
                                this.winningPos[reelIndex][symbolIndex] = true;
                            }

                            if (symbol.includes(`XN`)) {
                                if (
                                    !(
                                        symbolIndex > 0 &&
                                        (reel[symbolIndex - 1] === DDDSymbolNames.XN_1 ||
                                            reel[symbolIndex - 1] === DDDSymbolNames.XN_2)
                                    )
                                ) {
                                    this.winningPos[reelIndex][symbolIndex] = true;
                                }
                            }
                        });
                    }
                });
            }
        });

        if (
            data.mode === AbstractGameMode.RESPIN &&
            data.nextMode === AbstractGameMode.NORMAL &&
            !data.isRestoreState
        ) {
            data.xNudgeMultipliersCurrent.forEach((multiplier, index) => {
                if (
                    multiplier > 0 &&
                    xNudgeWinningPos[index]?.length > 0 &&
                    xNudgeWinningPos[index].includes(true)
                ) {
                    this.winningPos[index] = xNudgeWinningPos[index];
                }
            });
        }

        super.onGame(data);
    }

    public getBetPrice(): number {
        const hasFreeBets: boolean = DDD.apiPlugin.freeBets.hasFreeBets();
        const bet: string = DDD.apiPlugin.freeBets.getBet();
        const level: string = DDD.apiPlugin.betLevel.getLevel();

        const betLevel: string = hasFreeBets ? bet : level;
        let price: number = DDD.model.gameData?.playedBetValue || parseFloat(betLevel);

        if (this.data.wasFeatureBuy || this.data.boostedBet) {
            for (let index = 0; index < this.initData.featureBuyTimesBetValue.length; index++) {
                if (this.initData.featureBuyTimesBetValue[index].name === this.buyFeatureName) {
                    price *= this.initData.featureBuyTimesBetValue[index].price;
                    break;
                }
            }
        }

        return price;
    }

    private getAccumulatedRoundWin(): number {
        const acWinInit = this.initData.accumulatedRoundWin;
        const acWinGame = this.gameData ? this.gameData.accumulatedRoundWin : 0;

        return this.isRestore ? acWinInit : acWinGame;
    }

    private getSpinWin(): number {
        return this.isRestore ? 0 : this.gameData.win;
    }

    public isWinBelowStake(isAccumulated: boolean = true): boolean {
        const price = this.getBetPrice();
        const accumulatedRoundWin = isAccumulated
            ? this.getAccumulatedRoundWin()
            : this.getSpinWin();

        let isBelowWinStake = false;

        if (DDD.apiPlugin !== undefined) {
            isBelowWinStake = DDD.apiPlugin.gameClientConfiguration.belowStakeWinRestriction;
        }

        return isBelowWinStake && accumulatedRoundWin <= price;
    }
}
